import type { AxiosError, AxiosInstance, AxiosRequestConfig } from "axios";
import { requestLockOverrideReason } from "./lockOverridePrompt";

/**
 * Lock dates (J/665). When a save reaches a record in a locked period and the person
 * may override, the API answers 409 with code "period_locked" and the locked records.
 * This asks them to confirm and give a reason (LockOverrideModal), then sends the same request again with
 * the X-Lock-Override header. The API still checks who they are; the header only names
 * the records and the reason. Cancel hands the original error back to the screen.
 */

export const LOCK_OVERRIDE_HEADER = "X-Lock-Override";

export interface LockedRecord {
  key: string;
  label: string;
  date: string;
  lock_date: string;
  locked_by_xero: boolean;
}

interface PeriodLockedResponse {
  code: "period_locked";
  message: string;
  records: LockedRecord[];
}

const isPeriodLocked = (error: AxiosError): error is AxiosError<PeriodLockedResponse> =>
  error?.response?.status === 409 && (error.response.data as { code?: string })?.code === "period_locked";

/** Base64 of the UTF-8 JSON, so the reason can hold any character in a header. */
export const encodeLockOverride = (records: string[], reason: string): string => {
  const bytes = new TextEncoder().encode(JSON.stringify({ records, reason }));

  return btoa(String.fromCharCode(...bytes));
};

export const decodeLockOverride = (header: string): { records: string[]; reason: string } => {
  const bytes = Uint8Array.from(atob(header), (character) => character.charCodeAt(0));

  return JSON.parse(new TextDecoder().decode(bytes));
};

export const attachLockOverride = (
  axiosInstance: AxiosInstance,
  ask: (records: LockedRecord[], previousReason?: string) => Promise<string | null> = requestLockOverrideReason,
): void => {
  axiosInstance.interceptors.response.use(undefined, async (error: AxiosError) => {
    if (!isPeriodLocked(error) || !error.config) {
      return Promise.reject(error);
    }

    const config: AxiosRequestConfig = error.config;
    const headers = (config.headers ?? {}) as Record<string, string>;
    const previous = headers[LOCK_OVERRIDE_HEADER] ? decodeLockOverride(headers[LOCK_OVERRIDE_HEADER]) : null;

    const reason = await ask(error.response!.data.records, previous?.reason);

    if (reason === null) {
      return Promise.reject(error);
    }

    // A save can reach a second locked record only after the first is allowed, so the
    // records confirmed before are sent again with the new ones.
    const keys = Array.from(new Set([...(previous?.records ?? []), ...error.response!.data.records.map((record) => record.key)]));

    return axiosInstance.request({
      ...config,
      headers: { ...headers, [LOCK_OVERRIDE_HEADER]: encodeLockOverride(keys, reason) },
    });
  });
};
